<script lang="ts" setup>
    import EinvoicingSendingEvidence from '~/app/einvoicing/models/EinvoicingSendingEvidence';
    import { useEvent } from '~/composables/useEventBus';

    const evidence = ref<EinvoicingSendingEvidence | null>(null);

    const close = () => {
        useEvent(`invoicing:invoice:evidence-modal:close`);
    };

    useModal('invoicing:invoice:evidence-modal', (data: any) => {
        evidence.value = data?.evidence ?? null;
    });

    const { open } = useIntercom();
</script>

<template>
    <Modal
        ref="modal"
        name="invoicing:invoice:evidence-modal"
    >
        <div>
            <!-- Header -->
            <div :class="$theme('modal.title.container')">
                <div class="flex justify-center items-center space-x-2">
                    <h2 :class="$theme('modal.title.text')">
                        {{ $t('invoicing.einvoicing.evidence-modal.title') }}
                    </h2>
                    <CommonInfoBadge
                        class="hidden md:block"
                        :text="$t('app.common.badges_info.new')"
                    />
                </div>
            </div>
            <!-- Content -->
            <div :class="$theme('modal.padding')">
                <section class="mt-6 mb-3 py-3 px-4 bg-branding-ghost-blue rounded-[4px]">
                    <div class="flex flex-col gap-y-2">
                        <!-- Receiver ID -->
                        <div>
                            <label class="block text-sm text-branding-active-grey font-open-sans font-semibold mb-1">
                                {{ $t('invoicing.einvoicing.evidence-modal.receiver-id') }}
                            </label>
                            <div class="flex items-center justify-between bg-white rounded px-2 py-1 text-branding-mid-grey">
                                <span class="font-mono text-sm">{{ evidence?.receiverId }}</span>
                                <CopyToClipboard :value="evidence?.receiverId || ''">
                                    <i class="far fa-copy cursor-pointer"></i>
                                </CopyToClipboard>
                            </div>
                        </div>

                        <!-- Sender ID -->
                        <div>
                            <label class="block text-sm text-branding-active-grey font-open-sans font-semibold mb-1">
                                {{ $t('invoicing.einvoicing.evidence-modal.sender-id') }}
                            </label>
                            <div class="flex items-center justify-between bg-white rounded px-2 py-1 text-branding-mid-grey">
                                <span class="font-mono text-sm">{{ evidence?.senderId }}</span>
                                <CopyToClipboard :value="evidence?.senderId || ''">
                                    <i class="far fa-copy cursor-pointer"></i>
                                </CopyToClipboard>
                            </div>
                        </div>

                        <!-- Transmission ID -->
                        <div>
                            <label class="block text-sm text-branding-active-grey font-open-sans font-semibold mb-1">
                                {{ $t('invoicing.einvoicing.evidence-modal.transmission-id') }}
                            </label>
                            <div class="flex items-center justify-between bg-white rounded px-2 py-1 text-branding-mid-grey">
                                <span class="font-mono text-sm truncate">{{ evidence?.transmissionId }}</span>
                                <CopyToClipboard :value="evidence?.transmissionId || ''">
                                    <i class="far fa-copy cursor-pointer"></i>
                                </CopyToClipboard>
                            </div>
                        </div>
                    </div>

                    <div>
                        <!-- Download Buttons -->
                        <div class="flex flex-wrap gap-y-4 gap-2 mt-6">
                            <div
                                v-for="xml in evidence?.xmlModelFiles?.items"
                                :key="xml.getId()"
                            >
                                <a
                                    :href="xml.getDownloadUrl()"
                                    :class="!xml ? 'cursor-not-allowed opacity-50' : ''"
                                    class="flex-1 px-4 py-2 border text-branding-dark-grey border-branding-dark-grey rounded font-open-sans font-semibold text-[12px]"
                                    target="_blank"
                                >
                                    <i class="fa-light fa-file-export"></i>
                                    {{ $t('invoicing.einvoicing.evidence-modal.download-xml') }}
                                </a>
                            </div>
                            <div
                                v-for="ubl in evidence?.ublModelFiles?.items"
                                :key="ubl.getId()"
                            >
                                <a
                                    :href="ubl.getDownloadUrl()"
                                    :class="!ubl ? 'cursor-not-allowed opacity-50' : ''"
                                    class="flex-1 px-4 py-2 border text-branding-dark-grey border-branding-dark-grey rounded font-open-sans font-semibold text-[12px]"
                                    target="_blank"
                                >
                                    <i class="fa-light fa-file-export"></i>
                                    {{ $t('invoicing.einvoicing.evidence-modal.download-ubl') }}
                                </a>
                            </div>
                        </div>

                        <!-- Peppol Logo -->
                        <div class="flex justify-end">
                            <div class="max-w-9 min-w-9 pt-5 pb-1 text-right">
                                <img
                                    src="/peppol_logo_grey.svg"
                                    alt="peppol logo"
                                    class="w-full"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Support Message-->
                <p class="text-center text-sm font-open-sans text-branding-dark-grey">
                    {{ $t('invoicing.einvoicing.evidence-modal.support-message-start') }}
                    <a
                        class="text-branding-blue underline cursor-pointer"
                        @click="open"
                        >{{ $t('invoicing.einvoicing.evidence-modal.support-message-end') }}</a
                    >
                </p>

                <!-- Footer -->
                <section class="mt-8 mb-6 text-center">
                    <button
                        class="bg-branding-brand-yellow px-8 py-3 rounded-md min-w-[204px]"
                        @click="close()"
                    >
                        {{ $t('invoicing.einvoicing.evidence-modal.close') }}
                    </button>
                </section>
            </div>
        </div>
    </Modal>
</template>
